import {apiFetch, handleResponse, toQueryString} from "./ApiService";

const ERROR_MESSAGES = {
    fetchFail: 'Failed to fetch payment advices',
};

export interface PaymentAdvicesRequest {
    search?: string;
    merchantAccount?: string;
    transactionDateFrom?: string;
    transactionDateTo?: string;
    reportDate?: string;
    currency?: string;
    financialTransactionType?: string;
    status?: string;
    page?: number;
    size?: number;
    sort?: string[];
}

export interface FilterRequest {
    search?: string;
    merchantAccount?: string;
    transactionDateFrom?: string;
    transactionDateTo?: string;
    reportDate?: string;
    currency?: string;
    financialTransactionType?: string;
    status?: string;
    sort?: string[];
}

export interface PaymentAdviceSummary {
    entries: PaymentAdviceResponse[];
    totalElements: number;
}

export interface PaymentAdviceResponse {
    id: string;
    pspReference: string;
    merchantAccount: string;
    referenceDocument: string;
    amount: number;
    currency: string;
    transactionDate: string;
    reportDate: string;
    status: string;
    financialTransactionType: string;
    disbursementReference: string;
    isExternal: boolean;
}

export interface PaymentAdviceDetails {
    pspReference: string;
    disbursementReference: string;
    referenceDocument: string;
    amount: number;
    currency: string;
    transactionDate: string;
    paymentType: string;
    paymentMethod: string;
    status: string;
    merchantAccount: string;
    originalReference: string;
    reportDate: string;
    valueDate: string;
    statusUpdatedAt: string;
    internalNotes: string;
    originalCurrencyAmount: number;
    originalCurrency: string;
    surchargeAmountInTransCrcy: number;
    taxAmount: number;
    feeAmountInTransactionCurrency: number;
    transactionFeePercentage: number;
    financialTransactionType: string;
    directCapture: boolean;
    lastReason: string;
}

export interface PaymentAdviceFiltersResponse {
    currencies: string[];
    statuses: string[];
    types: string[];
    merchantAccounts: string[];
}

export const fetchPaymentAdvices = async (
    request: PaymentAdvicesRequest
): Promise<PaymentAdviceSummary> => {
    const qs = toQueryString(request);
    const response = await apiFetch(
        `/api/payment-advice${qs ? `?${qs}` : ""}`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
};

export const getPaymentAdviceDetails = async (
    paymentAdviceId: string
): Promise<PaymentAdviceDetails> => {
    const response = await apiFetch(
        `/api/payment-advice/${paymentAdviceId}`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
};

export const getPaymentAdviceFilters = async (): Promise<PaymentAdviceFiltersResponse> => {
    const response = await apiFetch(
        `/api/payment-advice/filters`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
}

export const downloadPaymentAdvices = async (
    request: FilterRequest
): Promise<Response> => {
    const qs = toQueryString(request);
    const response = await apiFetch(
        `/api/payment-advice/download${qs ? `?${qs}` : ""}`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response;
};

export const retryPaymentAdviceExport = async (
    paymentAdviceIds: string[]
): Promise<Map<string, string>> => {
    const response = await apiFetch(`/api/payment-advice/retry-export`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(paymentAdviceIds),
    });

    let body = await response.json();

    if (response.ok) {
        return new Map(Object.entries(body as Record<string, string>));
    }

    if (response.status >= 400 && response.status < 500) {
        const msg =
            body && typeof body === "object" && "error" in (body as any)
                ? (body as any).error
                : "Request failed";
        throw new Error(String(msg));
    }

    throw new Error("Retry export failed");
};